import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip, Warning } from '../../components/LessonUI';

const JSArrays = () => {
    const arrayBasicsCode = `// Standard Array Initialization
const frameworks = ['React', 'Next.js', 'Vue'];
const data = new Array(100).fill(0); // Pre-allocating for performance

// Fast Access (O(1))
console.log(frameworks[0]); // 'React'

// New access method (handles negative indexing)
console.log(frameworks.at(-1)); // 'Vue'`;

    const complexityCode = `// --- Performance Considerations ---

// O(1) Operations (Fast)
const stack = [1, 2, 3];
stack.push(4); // Add to end
stack.pop();    // Remove from end

// O(n) Operations (Potentially Slow)
const queue = [1, 2, 3];
queue.unshift(0); // Requires re-indexing all existing elements
queue.shift();   // Requires re-indexing all existing elements`;

    const transformationCode = `// The Modern Functional Trinity: map, filter, reduce
const metrics = [45, 92, 12, 88, 56];

// 1. Filter: Selection logic
const highPerformers = metrics.filter(m => m &gt; 80);

// 2. Map: Transformation logic
const labelMetrics = metrics.map(m => ({ score: m, passed: m &gt; 50 }));

// 3. Reduce: Aggregation logic (The Swiss Army Knife)
const sum = metrics.reduce((acc, current) => acc + current, 0);`;

    const immutabilityCode = `// ES2023 Non-mutating methods (Production Standard)
const original = [3, 1, 4, 1, 5];

// Instead of sort() which mutates the original:
const sorted = original.toSorted((a, b) => a - b);

// Instead of reverse() which mutates:
const reversed = original.toReversed();

console.log(original); // [3, 1, 4, 1, 5] (Safety preserved!)`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Algorithms & Data Structures</div>
                <h1>High-Performance Arrays</h1>
                <p className="hero-lead">
                    Arrays in JavaScript are dynamic, high-level structures that serve as
                    the backbone for data manipulation. Understanding the performance
                    trade-offs of different methods is key to writing scalable code.
                </p>
            </div>

            <div className="content-section">
                <h2>Memory and Allocation</h2>
                <p>
                    Unlike low-level languages, JavaScript arrays can grow dynamically.
                    However, the engine (V8) optimizes them into "Fixed-size" blocks
                    when elements are consistent. Using <code>Array.at()</code> provides
                    a modern, cleaner syntax for accessing elements from the end of
                    a collection.
                </p>
                <CodeBlock language="javascript" code={arrayBasicsCode} />
            </div>

            <div className="content-section">
                <h2>The Big O of Array Methods</h2>
                <p>
                    Not all array operations are created equal. Adding or removing from
                    the <strong>end</strong> of an array is highly efficient (O(1)),
                    whereas modifying the <strong>beginning</strong> requires the
                    engine to re-assign every single index in the array (O(n)).
                </p>
                <CodeBlock language="javascript" code={complexityCode} />
                <Warning><strong> Performance Warning:</strong> Be cautious when using
                    <code>shift()</code> or <code>unshift()</code> inside large loops,
                    as the re-indexing cost scales linearly with the array size.</Warning>
            </div>

            <div className="content-section">
                <h2>Declarative Data Transformation</h2>
                <p>
                    Modern JavaScript favors <strong>Declarative Patterns</strong>.
                    Rather than using <code>for</code> loops to mutate state, we use
                    higher-order methods to describe <em>what</em> we want to achieve.
                </p>
                <CodeBlock language="javascript" code={transformationCode} />
                <ProTip><strong> Pro Tip:</strong> <code>reduce</code> is the most
                    powerful methodit can replicate the behavior of <code>map</code>,
                    <code>filter</code>, and even <code>find</code>, while aggregating
                    results into any data structure (objects, maps, or even other arrays).</ProTip>
            </div>

            <div className="content-section">
                <h2>The Immutability Shift</h2>
                <p>
                    In React and Redux ecosystems, <strong>State Mutation</strong> is
                    a primary source of bugs. ES2023 introduced non-mutating versions
                    of classic methods, allowing you to sort or reverse data without
                    affecting the original reference.
                </p>
                <CodeBlock language="javascript" code={immutabilityCode} />
            </div>

            <KeyFacts><ul>
                    <li><strong>End vs Start:</strong> Favor <code>push/pop</code> over <code>shift/unshift</code> for performance.</li>
                    <li><strong>Immutability:</strong> Use <code>toSorted()</code> and <code>toReversed()</code> to avoid side-effects.</li>
                    <li><strong>Empty Checks:</strong> Use <code>arr.length === 0</code> to safely check for empty collections.</li>
                    <li> <strong>Mutation:</strong> Avoid <code>sort()</code> on props or shared state objects.</li>
                    <li><strong>Search:</strong> Use <code>includes()</code> for primitives and <code>some()/find()</code> for object searches.</li>
                    <li><strong>Pre-allocation:</strong> For large datasets, use <code>new Array(size).fill(0)</code> to help the engine pre-allocate heap memory.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSArrays;



